// folderAssignment.js
//
// Build 27 (item 1): per-user folder assignment for shared notes/diagrams.
//
// Problem: a note (or diagram) shared with you is ONE Firestore document
// with ONE folderId. If a recipient files it into their own folder by
// changing folderId, it would move in the owner's view too — and the
// owner may not even have that folder.
//
// Solution: an optional `folderAssignments` map on the item doc, keyed by
// userId → folderId. Each user's *effective* folder for an item is their
// personal assignment if present, otherwise the item's own folderId
// (which is what the owner uses, and the default for everyone else).
//
// No migration needed: absence of the map means "use legacy folderId",
// so every existing note/diagram keeps working unchanged.

/**
 * The folder a given user sees an item filed under.
 *
 * @param item    note or diagram doc (may have folderAssignments map)
 * @param userId  the current user's uid
 * @returns folderId string, or null for "no folder / root"
 */
export function effectiveFolderId(item, userId) {
  if (!item) return null;
  const assignments = item.folderAssignments;
  if (assignments && userId && Object.prototype.hasOwnProperty.call(assignments, userId)) {
    return assignments[userId] || null;
  }
  return item.folderId || null;
}

/**
 * Whether the given user owns the item (created it).
 * Owners change folderId directly; recipients use folderAssignments.
 */
export function isOwner(item, userId) {
  return !!item && !!userId && item.createdBy === userId;
}

/**
 * Build the Firestore update payload for moving an item to a folder for
 * a specific user. Owners update folderId (their canonical placement);
 * recipients update only their slot in folderAssignments so the owner
 * and other recipients are unaffected.
 *
 * @param item            the note/diagram doc
 * @param userId          current user's uid
 * @param targetFolderId  destination folder id, or null for root
 * @returns object suitable for passing to updateDoc()
 */
export function buildFolderMoveUpdate(item, userId, targetFolderId) {
  const dest = targetFolderId || null;
  if (isOwner(item, userId)) {
    return { folderId: dest };
  }
  // Recipient: write only their key in the map. Using dot-path notation
  // so we don't clobber other users' assignments.
  return { [`folderAssignments.${userId}`]: dest };
}
